/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Section-end feedback (FB3-FB11), shown at end of each section.
 * Also used for FB1 (before assessment) and FB2 (Before You Start).
 * Skip is available on every question in section-end feedback.
 */

const RATING_LABELS = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];
const MOOD_LABELS = ['Not great', '', '', '', 'Really good'];

/* FB1 — before assessment, platform landing */
export const FB1_QUESTION = {
  id: 'FB1', type: 'text', optional: true,
  question: 'What is your first impression of SPRING, before you begin?',
};

/* FB2 — Before You Start screen */
export const FB2_QUESTION = {
  id: 'FB2', type: 'rating',
  question: 'This page gave me a clear idea of what the assessment involves and what happens next.',
};

/* FB3-FB11 — end of each section (same 9 questions, four times) */
export const SECTION_END_QUESTIONS = [
  { id: 'FB3', type: 'rating', question: 'This section was easy to answer.' },
  { id: 'FB4', type: 'rating', question: 'The pace felt right. Not too fast, not too slow.' },
  { id: 'FB5', type: 'rating', question: 'Nothing in this section felt confusing.' },
  { id: 'FB6', type: 'rating', question: 'Nothing in this section felt intrusive or judgmental.' },
  { id: 'FB7', type: 'rating', question: 'The language used in this section was clear and plain, not full of jargon.' },
  { id: 'FB8', type: 'yesno', question: 'Is there anything else about this section you want to tell us?' },
  { id: 'FB9', type: 'text', optional: true, conditionalOn: 'FB8_Yes',
    question: 'If yes, what?' },
  { id: 'FB10', type: 'mood', question: 'How did you feel at the end of this section?' },
  { id: 'FB11', type: 'text', optional: true, question: 'Anything you would like to add about how this section felt?' },
];


/**
 * SectionEndFeedback — renders the 9 questions for end-of-section feedback.
 * Props: attemptId, sectionNumber (1-4), checkpoint, onComplete, onSkip
 */
export function SectionEndFeedback({ attemptId, sectionNumber, checkpoint, onComplete, onSkip }) {
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [fb8Yes, setFb8Yes] = useState(false);

  const set = (id, val) => {
    setResponses(prev => ({ ...prev, [id]: val }));
    if (id === 'FB8') setFb8Yes(val === 'Yes');
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const saves = Object.entries(responses).map(([qid, val]) => {
        const q = SECTION_END_QUESTIONS.find(sq => sq.id === qid) || {};
        return axios.post(`${API}/api/feedback-module/${attemptId}`, {
          question_id: `${qid}_S${sectionNumber}`,
          checkpoint: checkpoint || `section_${sectionNumber}`,
          response_type: q.type === 'text' ? 'text' : q.type === 'mood' ? 'mood' : q.type === 'yesno' ? 'yesno' : 'rating',
          rating_value: (q.type === 'rating' || q.type === 'mood') ? val : undefined,
          text_value: q.type === 'text' ? val : q.type === 'yesno' ? val : undefined,
        });
      });
      await Promise.all(saves);
    } catch (e) {
      console.error('Error saving section feedback:', e);
    }
    setSaving(false);
    onComplete?.();
  };

  return (
    <div className="sfb-container" data-testid={`section-feedback-${sectionNumber}`}>
      <div className="sfb-inner">
        <h3 className="sfb-heading">Quick feedback on this section</h3>
        <p className="sfb-subtitle">Your answers help us improve SPRING. You can skip any question.</p>

        {SECTION_END_QUESTIONS.map(q => {
          if (q.conditionalOn === 'FB8_Yes' && !fb8Yes) return null;

          return (
            <div key={q.id} className="sfb-question" data-testid={`sfb-${q.id}`}>
              <p className="sfb-question-text">{q.question}</p>

              {q.type === 'rating' && (
                <div className="sfb-rating-row">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      className={`fb-rating-btn ${responses[q.id] === n ? 'active' : ''}`}
                      onClick={() => set(q.id, n)}
                      title={RATING_LABELS[n - 1]}
                    >{n}</button>
                  ))}
                  <span className="fb-rating-labels">
                    <span>Strongly disagree</span>
                    <span>Strongly agree</span>
                  </span>
                </div>
              )}

              {q.type === 'mood' && (
                <div className="sfb-rating-row">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      className={`fb-rating-btn ${responses[q.id] === n ? 'active' : ''}`}
                      onClick={() => set(q.id, n)}
                      title={MOOD_LABELS[n - 1] || `${n}`}
                    >{n}</button>
                  ))}
                  <span className="fb-rating-labels">
                    <span>Not great</span>
                    <span>Really good</span>
                  </span>
                </div>
              )}

              {q.type === 'yesno' && (
                <div className="fb-yesno-row">
                  <button className={`fb-yesno-btn ${responses[q.id] === 'Yes' ? 'active' : ''}`} onClick={() => set(q.id, 'Yes')}>Yes</button>
                  <button className={`fb-yesno-btn ${responses[q.id] === 'No' ? 'active' : ''}`} onClick={() => set(q.id, 'No')}>No</button>
                </div>
              )}

              {q.type === 'text' && (
                <textarea
                  className="fb-textarea"
                  value={responses[q.id] || ''}
                  onChange={e => set(q.id, e.target.value)}
                  placeholder="Optional"
                  rows={3}
                />
              )}
            </div>
          );
        })}

        <div className="sfb-actions">
          <button className="rpt-btn-primary" onClick={handleSubmit} disabled={saving} data-testid="section-feedback-continue-btn">
            {saving ? 'Saving...' : 'Continue'}
          </button>
          <button className="rpt-btn-outline" onClick={onSkip} style={{ marginLeft: 12 }} data-testid="section-feedback-skip-btn">
            Skip feedback
          </button>
        </div>
      </div>
    </div>
  );
}
